// src/components/History.jsx
// Admin + limited user access to edit and delete entries with full audit trail

import { useState } from "react";
import { OILS, SKUS, SKU_LITRES } from "../constants";
import { fmt, fmtL, fmtKg, today, OilDot, PayPill, S, TabRow, EmptyState, AlertBar } from "../utils/ui";

const canEditEntry = (user, entry) => {
  if (user.admin) return true;
  // Plant/shop users can only edit their own entries from today
  return entry.by === user.name && entry.date === today();
};

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center",
      padding: "0 20px",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "24px 20px",
        width: "100%", maxWidth: 380,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Are you sure?</div>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>{message}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel}
            style={{ ...S.btn("#F7F4EE", "#555"), flex: 1, border: "1px solid #E0DDD5" }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ ...S.btn("#A32D2D", "#fff"), flex: 1 }}>
            Yes, delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── EDIT MODALS ───────────────────────────────────────────────────────────────

function EditPurchase({ entry, onSave, onClose }) {
  const [oil, setOil]       = useState(entry.oil);
  const [kgs, setKgs]       = useState(entry.kgs);
  const [price, setPrice]   = useState(entry.price);
  const [date, setDate]     = useState(entry.date);
  const [supplier, setSupplier] = useState(entry.supplier || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave({ ...entry, oil, kgs: Number(kgs), price: Number(price), date, supplier });
    setSaving(false);
  };

  return (
    <div style={modalWrap}>
      <div style={modalBox}>
        <div style={modalTitle}>Edit Purchase</div>
        <label style={S.label}>Oil seed</label>
        <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
          {OILS.map(o => <option key={o}>{o}</option>)}
        </select>
        <label style={S.label}>Quantity (kg)</label>
        <input style={S.input} type="number" value={kgs} onChange={e => setKgs(e.target.value)} />
        <label style={S.label}>Price per kg (₹)</label>
        <input style={S.input} type="number" value={price} onChange={e => setPrice(e.target.value)} />
        <label style={S.label}>Supplier</label>
        <input style={S.input} value={supplier} onChange={e => setSupplier(e.target.value)} />
        <label style={S.label}>Date</label>
        <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...S.btn("#F7F4EE", "#555"), flex: 1, border: "1px solid #E0DDD5" }} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn(), flex: 1 }} onClick={save} disabled={saving}>{saving ? "Saving..." : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function EditSale({ entry, state, onSave, onClose }) {
  const [oil, setOil]         = useState(entry.oil);
  const [sku, setSku]         = useState(entry.sku);
  const [qty, setQty]         = useState(entry.qty);
  const [mode, setMode]       = useState(entry.mode);
  const [location, setLoc]    = useState(entry.location);
  const [price, setPrice]     = useState(entry.amount / entry.qty);
  const [date, setDate]       = useState(entry.date);
  const [saving, setSaving]   = useState(false);

  const litres = Number(qty) * SKU_LITRES[sku];
  const amount = Number(qty) * Number(price);

  const save = async () => {
    setSaving(true);
    await onSave({ ...entry, oil, sku, qty: Number(qty), litres, amount, mode, location, date });
    setSaving(false);
  };

  return (
    <div style={modalWrap}>
      <div style={modalBox}>
        <div style={modalTitle}>Edit Sale</div>
        <label style={S.label}>Oil type</label>
        <select style={S.select} value={oil} onChange={e => setOil(e.target.value)}>
          {OILS.map(o => <option key={o}>{o}</option>)}
        </select>
        <label style={S.label}>Pack size</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {SKUS.map(s => <div key={s} onClick={() => setSku(s)} style={S.skuBtn(sku === s)}>{s}</div>)}
        </div>
        <label style={S.label}>Quantity (units)</label>
        <input style={S.input} type="number" value={qty} onChange={e => setQty(e.target.value)} />
        <label style={S.label}>Price per {sku} (₹)</label>
        <input style={S.input} type="number" value={price} onChange={e => setPrice(e.target.value)} />
        <label style={S.label}>Location</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {["Plant", "Shop"].map(l => (
            <div key={l} onClick={() => setLoc(l)} style={{ ...S.skuBtn(location === l), padding: "8px 20px" }}>{l}</div>
          ))}
        </div>
        <label style={S.label}>Payment mode</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {["Cash", "UPI", "Credit"].map(m => (
            <div key={m} onClick={() => setMode(m)} style={S.payBtn(mode === m, m)}>{m}</div>
          ))}
        </div>
        <label style={S.label}>Date</label>
        <input style={S.input} type="date" value={date} onChange={e => setDate(e.target.value)} />
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...S.btn("#F7F4EE", "#555"), flex: 1, border: "1px solid #E0DDD5" }} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn(), flex: 1 }} onClick={save} disabled={saving}>{saving ? "Saving..." : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL STYLES ──────────────────────────────────────────────────────────────
const modalWrap = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
  zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center",
};
const modalBox = {
  background: "#fff", borderRadius: "16px 16px 0 0",
  padding: "20px 16px 32px", width: "100%", maxWidth: 430,
  maxHeight: "90vh", overflowY: "auto",
};
const modalTitle = {
  fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#1a1a1a",
};

// ── ENTRY CARD ────────────────────────────────────────────────────────────────
function EntryCard({ entry, type, user, onEdit, onDelete }) {
  const canEdit = canEditEntry(user, entry);

  const labels = {
    purchase: `${entry.kgs} kg · ₹${entry.price}/kg · ${entry.supplier || ""}`,
    batch:    `Used ${fmtKg(entry.rawUsed)} → ${fmtL(entry.oilLitres)} oil + ${fmtKg(entry.cakeKg)} cake`,
    transfer: `${entry.count} × ${entry.sku} · ${fmtL(entry.litres)}`,
    sale:     `${entry.qty} × ${entry.sku} · ${fmtL(entry.litres)} · ${entry.location}`,
    cakeSale: `${fmtKg(entry.kg)} · ${entry.location || "Plant"}`,
  };

  const amounts = {
    purchase: fmt(entry.kgs * entry.price),
    batch:    `${fmtL(entry.oilLitres)} produced`,
    transfer: fmt(entry.value),
    sale:     fmt(entry.amount),
    cakeSale: fmt(entry.amount),
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "1px solid #E8E4DC",
      padding: "10px 14px", marginBottom: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <OilDot oil={entry.oil} />
          <strong style={{ fontSize: 13 }}>{entry.oil}</strong>
          {entry.mode && <PayPill mode={entry.mode} />}
        </div>
        <strong style={{ fontSize: 13 }}>{amounts[type]}</strong>
      </div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: canEdit ? 8 : 0 }}>
        {labels[type]} · {entry.date} · {entry.by}
      </div>
      {canEdit && (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => onEdit(entry)}
            style={{
              flex: 1, padding: "6px", borderRadius: 6, fontSize: 12,
              background: "#E6F1FB", color: "#185FA5", border: "none", cursor: "pointer",
            }}>
            ✏️ Edit
          </button>
          <button onClick={() => onDelete(entry)}
            style={{
              flex: 1, padding: "6px", borderRadius: 6, fontSize: 12,
              background: "#FCEBEB", color: "#A32D2D", border: "none", cursor: "pointer",
            }}>
            🗑️ Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── MAIN HISTORY COMPONENT ────────────────────────────────────────────────────
export function History({ state, actions, user }) {
  const [tab, setTab]       = useState("sales");
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [deleteType, setDeleteType]   = useState(null);
  const [msg, setMsg]       = useState("");

  const tabs = [
    { id: "sales",     label: "Sales"     },
    { id: "purchases", label: "Purchases" },
    { id: "batches",   label: "Batches"   },
    { id: "transfers", label: "Transfers" },
    { id: "cakeSales", label: "Cake"      },
  ];

  const showMsg = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  // ── DELETE ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    await actions.deleteEntry(deleteEntry, deleteType, user);
    setDeleteEntry(null); setDeleteType(null);
    showMsg("Entry deleted and stock updated.");
  };

  // ── EDIT SAVE ───────────────────────────────────────────────────────────────
  const handleEditSave = async (updated) => {
    await actions.editEntry(updated, tab, user);
    setEditEntry(null);
    showMsg("Entry updated successfully.");
  };

  const entries = {
    sales:     [...(state.sales     || [])].reverse(),
    purchases: [...(state.purchases || [])].reverse(),
    batches:   [...(state.batches   || [])].reverse(),
    transfers: [...(state.transfers || [])].reverse(),
    cakeSales: [...(state.cakeSales || [])].reverse(),
  };

  return (
    <div style={{ padding: "12px 14px" }}>
      {!user.admin && (
        <AlertBar text="You can edit/delete only your own entries from today. Admins can edit all entries." type="info" />
      )}
      {msg && <AlertBar text={msg} type="success" />}

      <TabRow tabs={tabs} active={tab} onChange={setTab} />

      {entries[tab].length === 0
        ? <EmptyState text={`No ${tab} entries yet`} />
        : entries[tab].map(entry => (
          <EntryCard
            key={entry.ts}
            entry={entry}
            type={tab === "cakeSales" ? "cakeSale" : tab.slice(0, -1)}
            user={user}
            onEdit={e => setEditEntry(e)}
            onDelete={e => { setDeleteEntry(e); setDeleteType(tab); }}
          />
        ))
      }

      {/* Edit modal */}
      {editEntry && tab === "sales" && (
        <EditSale entry={editEntry} state={state} onSave={handleEditSave} onClose={() => setEditEntry(null)} />
      )}
      {editEntry && tab === "purchases" && (
        <EditPurchase entry={editEntry} onSave={handleEditSave} onClose={() => setEditEntry(null)} />
      )}

      {/* Delete confirmation */}
      {deleteEntry && (
        <ConfirmModal
          message={`This will delete the ${deleteEntry.oil} entry by ${deleteEntry.by} on ${deleteEntry.date} and reverse the stock change.`}
          onConfirm={handleDelete}
          onCancel={() => { setDeleteEntry(null); setDeleteType(null); }}
        />
      )}
    </div>
  );
}
